'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { Badge } from '@workspace/ui/components/badge';
import { Button } from '@workspace/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@workspace/ui/components/dialog';
import {
  formatAmount,
  formatConversionRate,
  formatTransferTypeLabel,
  TRANSFER_TYPE_LABELS,
} from '@workspace/ui/lib';
import { format } from 'date-fns';
import {
  CheckCircle2,
  CircleAlert,
  CircleMinus,
  Clock,
  Download,
  FileSpreadsheet,
  Info,
  PlusIcon,
  XCircle,
} from 'lucide-react';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import {
  useDownloadTransactionInvoice,
  useDownloadTransactionReceipt,
} from '@/hooks/use-transactions';
import { useRestrictionGate } from '@/hooks/use-user-restriction';
import type { ExpenseCategoryOption } from '@/types/expense';
import { RestrictionDialog } from '../restrictions/restriction-dialog';
import { CancelTransactionDialog } from './cancel-transaction-dialog';
import { CancelTransactionSuccessDialog } from './cancel-transaction-success-dialog';
import type { Transaction, TransactionStatus } from './transactions-table';

interface TransactionDetailsDialogProps {
  transaction: Transaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expenseCategories: ExpenseCategoryOption[];
  fromEntity?: boolean;
}

const getStatusIcon = (status: TransactionStatus) => {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'pending':
      return <Clock className="text-grey-600 h-5 w-5" />;
    case 'failed':
      return <CircleAlert className="h-5 w-5 text-red-500" />;
    case 'declined':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'cancelled':
      return <CircleMinus className="h-5 w-5 text-red-500" />;
    case 'reserved':
      return <Clock className="h-5 w-5 text-blue-500" />;
    case 'refunded':
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    case 'queued':
      return <Clock className="h-5 w-5 text-orange-500" />;
    default:
      return <Clock className="text-grey-500 h-5 w-5" />;
  }
};

const getStatusBadgeVariant = (status: TransactionStatus) => {
  switch (status) {
    case 'completed':
      return 'green';
    case 'pending':
      return 'dark-grey';
    case 'failed':
      return 'red';
    case 'declined':
      return 'red';
    case 'cancelled':
      return 'red';
    case 'reserved':
      return 'light-blue-2';
    case 'refunded':
      return 'green';
    case 'queued':
      return 'orange';
    default:
      return 'light-grey';
  }
};

const REWARD_TRANSACTION_TYPES = new Set(['balance-reward', 'rewards', 'pre_payment_rewards']);

function isRewardsTransaction(transaction: Pick<Transaction, 'type' | 'transfer_type'>): boolean {
  const type = transaction.type?.toLowerCase() ?? '';
  const transferType = transaction.transfer_type?.toLowerCase() ?? '';

  return REWARD_TRANSACTION_TYPES.has(type) || REWARD_TRANSACTION_TYPES.has(transferType);
}

// Get user-friendly display label for the transaction type header
const getTransactionTypeLabel = (
  type: string,
  transferType?: string,
  isIncoming?: boolean
): string => {
  if (transferType) {
    const lowerTransferType = transferType.toLowerCase();

    // Special case for team-transfer
    if (lowerTransferType === 'team-transfer') {
      return isIncoming ? 'Received' : 'Sent';
    }

    // Special case for expense
    if (lowerTransferType === 'expense') {
      return isIncoming ? 'Reimbursement' : 'Expense';
    }

    if (lowerTransferType === 'batch-transfer') {
      return 'Wallet to wallet';
    }

    return TRANSFER_TYPE_LABELS[lowerTransferType] || formatTransferTypeLabel(transferType);
  }

  // Fallback to type
  const lowerType = type.toLowerCase();
  switch (lowerType) {
    case 'inbound':
    case 'top-up':
      return 'Deposit';
    case 'outbound':
    case 'withdrawal':
      return 'Withdrawal';
    case 'internal':
    case 'wallet-to-wallet':
      return 'Internal transfer';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

const getTransactionTypeDescription = (
  type: string,
  provider: string,
  bankHolderName?: string,
  transferType?: string,
  isIncoming?: boolean,
  _description?: string,
  _userName?: string,
  reason?: string
) => {
  // For transfer_type 'other', description is the reason (or '-' if missing)
  if (transferType?.toLowerCase() === 'other') {
    return reason?.trim() ?? '-';
  }

  // Priority 1: Use transfer_type for meaningful user-facing label
  if (transferType) {
    const lowerTransferType = transferType.toLowerCase();

    // Special case for team-transfer
    if (lowerTransferType === 'team-transfer') {
      return 'Team transfer';
    }

    // Special case for expense
    if (lowerTransferType === 'expense') {
      return isIncoming ? 'Expense reimbursement' : 'Expense payment';
    }

    if (lowerTransferType === 'batch-transfer') {
      return 'Batch transfer';
    }

    if (lowerTransferType === 'bank-withdraw' || lowerTransferType === 'bank-withdrawal') {
      if (provider === 'card') {
        return 'To card';
      }
      return bankHolderName ? `To ${bankHolderName}` : 'Bank withdrawal';
    }

    // Default: use label map or format the transfer type
    return TRANSFER_TYPE_LABELS[lowerTransferType] || formatTransferTypeLabel(transferType);
  }

  // Fallback for legacy or missing transfer_type - use type field
  const lowerType = type.toLowerCase();

  if (lowerType === 'rewards') {
    return 'Balance rewards';
  }

  if (lowerType === 'top-up' || lowerType === 'inbound') {
    return 'Deposit';
  }

  if (lowerType === 'withdrawal' || lowerType === 'outbound') {
    if (provider === 'card') {
      return 'To card';
    }
    return bankHolderName ? `To ${bankHolderName}` : 'Withdrawal';
  }

  if (lowerType === 'deposit') {
    return 'Deposit';
  }

  if (lowerType === 'wallet-to-wallet' || lowerType === 'internal') {
    return 'Currency conversion';
  }

  return type.charAt(0).toUpperCase() + type.slice(1);
};

function positiveStoredAmount(value: string | number | null | undefined): string | number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return value;
}

/**
 * Outgoing rows show the credited amount in `to_currency`.
 * That credit is `converted_amount`. Rebuilding it from the rate produced 490.50
 * when the wallet was actually credited 457.88.
 * Incoming rows show the source amount in `from_currency`, so they still divide
 * by the rate. The rate formatter keeps enough precision for that division.
 */
function conversionCounterpartAmount(
  transaction: Transaction,
  isNegative: boolean
): string | number {
  if (isNegative) {
    return (
      positiveStoredAmount(transaction.converted_amount) ??
      positiveStoredAmount(transaction.total_converted_amount) ??
      transaction.total_converted_amount
    );
  }

  return transaction.numericAmount / (transaction.conversion_rate || 1);
}

// Helper to determine if a transaction type is negative (withdrawal) or positive
const isNegativeTransactionType = (
  type: string,
  currencyId: number,
  toCurrencyId: number,
  transferType?: string,
  isIncoming?: boolean
): boolean => {
  // Use is_incoming as the source of truth when available
  if (typeof isIncoming === 'boolean') {
    return !isIncoming;
  }

  // Fallback to type-based logic
  const lowerType = type.toLowerCase();
  switch (lowerType) {
    case 'withdrawal':
    case 'outbound':
      return true;
    case 'wallet-to-wallet':
    case 'internal':
      if (transferType === 'team-transfer') {
        return !isIncoming;
      }
      return currencyId !== toCurrencyId;
    default:
      return false;
  }
};

const getTransactionStatus = (apiStatus: string): TransactionStatus => {
  switch (apiStatus.toLowerCase()) {
    case 'declined':
      return 'declined';
    case 'pending':
      return 'pending';
    case 'failed':
      return 'failed';
    case 'completed':
      return 'completed';
    case 'reserved':
      return 'reserved';
    case 'cancelled':
      return 'cancelled';
    case 'refunded':
      return 'refunded';
    case 'queued':
      return 'queued';
    default:
      return 'completed';
  }
};

// ============================================
// EXPENSE TRANSACTION CONTENT
// ============================================

const EXPENSE_PAYMENT_DESCRIPTION_PREFIXES = [
  'Bulk expense payment: ',
  'Expense payment: ',
] as const;

function getExpenseTransactionDescriptionLine(
  description: string | undefined,
  categories: ExpenseCategoryOption[]
): string {
  if (!description?.trim()) return '';
  for (const prefix of EXPENSE_PAYMENT_DESCRIPTION_PREFIXES) {
    if (description.startsWith(prefix)) {
      const slug = description.slice(prefix.length).trim();
      if (!slug) return prefix;
      const label = categories.find((c) => c.value === slug)?.name ?? slug;
      return `${prefix}${label}`;
    }
  }
  return description;
}

interface ExpenseContentProps {
  transaction: Transaction;
  transactionStatus: TransactionStatus;
  isNegative: boolean;
  expenseCategories: ExpenseCategoryOption[];
  canShowInvoiceButton: boolean;
  downloadInvoiceMutation: ReturnType<typeof useDownloadTransactionInvoice>;
  onDownloadInvoice: () => void;
}

function ExpenseTransactionContent({
  transaction,
  transactionStatus,
  isNegative,
  expenseCategories,
  canShowInvoiceButton,
  downloadInvoiceMutation,
  onDownloadInvoice,
}: ExpenseContentProps) {
  const expenseDescription = useMemo(
    () => getExpenseTransactionDescriptionLine(transaction.description, expenseCategories),
    [transaction.description, expenseCategories]
  );

  return (
    <div className="flex w-full flex-col space-y-5">
      {/* Header */}
      <div className="space-y-2">
        <div className="text-grey-600 dark:text-grey-400 text-base font-medium">Reimbursement</div>
        <div className="text--foreground text-[26px] font-bold dark:text-neutral-100">
          {isNegative ? '-' : '+'}
          {formatAmount(Math.abs(transaction.numericAmount))} {transaction.currency}
        </div>
      </div>

      {/* Expense Description */}
      {expenseDescription && (
        <div className="text--foreground text-base font-medium dark:text-neutral-100">
          {expenseDescription}
        </div>
      )}

      {/* Details */}
      <div className="space-y-4">
        {/* Status */}
        <div className="flex items-center justify-between">
          <span className="text-grey-600 dark:text-grey-400 text-base font-medium">Status</span>
          <div className="flex items-center gap-2">
            {getStatusIcon(transactionStatus)}
            <Badge
              size="lg"
              variant={getStatusBadgeVariant(transactionStatus)}
              text={transactionStatus}
              className="capitalize"
            />
          </div>
        </div>

        {/* Reimbursement method */}
        <div className="flex items-center justify-between">
          <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
            Reimbursement method
          </span>
          <span className="text-grey-800 text-base font-medium dark:text-neutral-100">Wallet</span>
        </div>

        {/* Payment date */}
        {transaction.date_of_payment && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              Payment date
            </span>
            <span className="text-grey-800 text-right text-base font-medium dark:text-neutral-100">
              {format(new Date(transaction.date_of_payment), 'dd/MM/yyyy')}
            </span>
          </div>
        )}

        {/* Note */}
        {transaction.note && (
          <div className="flex items-start justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">Note</span>
            <span className="text-grey-800 max-w-[300px] text-right text-base font-medium dark:text-neutral-100">
              {transaction.note}
            </span>
          </div>
        )}
      </div>

      {canShowInvoiceButton && (
        <Button
          variant="primary"
          className="w-full"
          onClick={onDownloadInvoice}
          isLoading={downloadInvoiceMutation.isPending}
          loadingText={'Downloading invoice'}
        >
          Download invoice
        </Button>
      )}
    </div>
  );
}

// ============================================
// REGULAR TRANSACTION CONTENT
// ============================================
interface TransactionContentProps {
  transaction: Transaction;
  transactionStatus: TransactionStatus;
  transactionTypeDescription: string;
  isNegative: boolean;
  shouldStrikeThrough: boolean;
  canShowInvoiceButton: boolean;
  canShowDownloadReceiptButton: boolean;
  showCancelButton: boolean;
  downloadInvoiceMutation: ReturnType<typeof useDownloadTransactionInvoice>;
  downloadReceiptMutation: ReturnType<typeof useDownloadTransactionReceipt>;
  onDownloadInvoice: () => void;
  onDownloadReceipt: () => void;
  onCancelClick: () => void;
  fromEntity?: boolean;
}

function RegularTransactionContent({
  transaction,
  transactionStatus,
  transactionTypeDescription,
  isNegative,
  shouldStrikeThrough,
  canShowInvoiceButton,
  canShowDownloadReceiptButton,
  showCancelButton,
  downloadInvoiceMutation,
  downloadReceiptMutation,
  onDownloadInvoice,
  onDownloadReceipt,
  onCancelClick,
  fromEntity,
}: TransactionContentProps) {
  const { guardNavigation, restrictionDialogProps } = useRestrictionGate();
  const shouldShowPaymentMethod =
    !isRewardsTransaction(transaction) &&
    transaction.transfer_type?.toLowerCase() !== 'currency-conversion' &&
    (transaction.cardNumber || ['top-up', 'inbound'].includes(transaction.type));
  const transactionTypeLabel = getTransactionTypeLabel(
    transaction.type,
    transaction.transfer_type,
    transaction.is_incoming
  );

  return (
    <div className="flex w-full flex-col space-y-5">
      {/* Transaction Type and Amount */}
      <div className="space-y-2">
        <div className="text-grey-600 dark:text-grey-400 text-base font-medium first-letter:capitalize">
          {transactionTypeLabel}
        </div>
        <div
          className={`text--foreground text-[26px] font-bold dark:text-neutral-100 ${
            shouldStrikeThrough ? 'line-through' : ''
          }`}
        >
          {isNegative ? '-' : '+'}
          {formatAmount(Math.abs(transaction.numericAmount))} {transaction.currency}
        </div>
      </div>

      {/* Transaction Description */}
      <div className="text--foreground text-base font-medium dark:text-neutral-100">
        {transactionTypeDescription}
      </div>

      {/* Transaction Details */}
      <div className="space-y-4">
        {/* Status */}
        <div className="flex items-center justify-between">
          <span className="text-grey-600 dark:text-grey-400 text-base font-medium">Status</span>
          <div className="flex items-center gap-2">
            {getStatusIcon(transactionStatus)}
            <Badge
              size="lg"
              variant={getStatusBadgeVariant(transactionStatus)}
              text={transactionStatus}
              className="capitalize"
            />
          </div>
        </div>

        {/* Transaction id */}
        {transaction.id && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              Transaction ID
            </span>
            <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
              {transaction.id}
            </span>
          </div>
        )}

        {/* Request date */}
        {transaction.date_of_request &&
          transaction.provider !== 'card' &&
          !['rewards', 'wallet-to-wallet', 'internal'].includes(transaction.type) && (
            <div className="flex items-center justify-between">
              <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
                Request date
              </span>
              <span className="text-grey-800 text-right text-base font-medium dark:text-neutral-100">
                {format(new Date(transaction.date_of_request), 'dd/MM/yyyy HH:mm')}
              </span>
            </div>
          )}

        {/* Request date (queued batch transfer) */}
        {transactionStatus === 'queued' && transaction.date_of_request && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              Request date
            </span>
            <span className="text-grey-800 text-right text-base font-medium dark:text-neutral-100">
              {format(new Date(transaction.date_of_request), 'dd/MM/yyyy HH:mm')}
            </span>
          </div>
        )}

        {/* Number of recipients (queued batch transfer) */}
        {transactionStatus === 'queued' && transaction.numberOfRecipients && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              Number of recipients
            </span>
            <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
              {transaction.numberOfRecipients}
            </span>
          </div>
        )}

        {/* Decline date */}
        {transaction.date_of_rejection && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              {transactionStatus === 'cancelled' ? 'Cancellation date' : 'Date of decline'}
            </span>
            <span className="text-grey-800 text-right text-base font-medium dark:text-neutral-100">
              {format(new Date(transaction.date_of_rejection), 'dd/MM/yyyy HH:mm')}
            </span>
          </div>
        )}

        {/* Payment date */}
        {transaction.date_of_payment && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              Payment date
            </span>
            <span className="text-grey-800 text-right text-base font-medium dark:text-neutral-100">
              {format(new Date(transaction.date_of_payment), 'dd/MM/yyyy HH:mm')}
            </span>
          </div>
        )}

        {/* Decline note */}
        {transaction.note && transactionStatus === 'declined' && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              Decline note
            </span>
            <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
              {transaction.note || '-'}
            </span>
          </div>
        )}

        {/* Payment method */}
        {shouldShowPaymentMethod && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              Payment method
            </span>
            <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
              {transaction.cardNumber
                ? transaction.cardNumber
                : transaction.provider === 'card'
                  ? 'Card'
                  : 'Bank transfer'}
            </span>
          </div>
        )}

        {/* Transfer description */}
        {transaction.description &&
          ['wallet-to-wallet', 'internal'].includes(transaction.type) &&
          transaction.transfer_type === 'team-transfer' && (
            <div className="flex items-center justify-between">
              <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
                Transfer description
              </span>
              <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
                {transaction.description}
              </span>
            </div>
          )}

        {/* Top-up purpose */}
        {transaction.description &&
          ['top-up', 'inbound'].includes(transaction.type) &&
          transaction.provider === 'bank' && (
            <div className="flex items-center justify-between">
              <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
                Top-up purpose
              </span>
              <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
                {transaction.description}
              </span>
            </div>
          )}

        {/* Fee */}
        {transaction.fees && transaction.fees.value > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">Fee</span>
            <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
              {formatAmount(transaction.fees.value)} {transaction.currency}
            </span>
          </div>
        )}

        {/* IBAN or Local account */}
        {transaction.bank_account && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              IBAN or local account
            </span>
            <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
              {transaction.bank_account}
            </span>
          </div>
        )}

        {/* Tax */}
        {transaction.tax > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">Tax</span>
            <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
              {formatAmount(transaction.tax_converted)} {transaction.to_currency}
            </span>
          </div>
        )}

        {/* Amount received */}
        {['withdrawal', 'outbound'].includes(transaction.type) &&
          transaction.provider !== 'card' &&
          transaction.total_converted_amount && (
            <div className="flex items-center justify-between">
              <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
                Amount received
              </span>
              <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
                {formatAmount(transaction.total_converted_amount)} {transaction.to_currency}
              </span>
            </div>
          )}

        {/* Converted amount */}
        {['wallet-to-wallet'].includes(transaction.type) &&
          transaction.transfer_type !== 'team-transfer' &&
          (isNegative
            ? (positiveStoredAmount(transaction.converted_amount) ??
              transaction.total_converted_amount)
            : transaction.numericAmount) && (
            <div className="flex items-center justify-between">
              <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
                {isNegative ? 'Converted amount' : 'Exchanged amount'}
              </span>
              <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
                {formatAmount(conversionCounterpartAmount(transaction, isNegative))}{' '}
                {isNegative ? transaction.to_currency : transaction.from_currency}
              </span>
            </div>
          )}

        {/* Amount received - team transfer */}
        {['wallet-to-wallet', 'internal'].includes(transaction.type) &&
          transaction.transfer_type === 'team-transfer' &&
          isNegative &&
          transaction.total_converted_amount && (
            <div className="flex items-center justify-between">
              <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
                Amount received
              </span>
              <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
                {formatAmount(transaction.total_converted_amount)} {transaction.to_currency}
              </span>
            </div>
          )}

        {/* Conversion rate */}
        {['wallet-to-wallet', 'internal'].includes(transaction.type) &&
          transaction.conversion_rate &&
          transaction.conversion_rate !== 1 &&
          transaction.to_currency !== transaction.currency && (
            <div className="flex items-center justify-between">
              <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
                Conversion rate
              </span>
              <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
                1 {transaction.from_currency} = {formatConversionRate(transaction.conversion_rate)}{' '}
                {transaction.to_currency}
              </span>
            </div>
          )}

        {/* EPS Card Number */}
        {transaction?.epsCardNumber && (
          <div className="flex items-center justify-between">
            <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
              Card number
            </span>
            <span className="text-grey-800 text-base font-medium dark:text-neutral-100">
              {transaction.epsCardNumber}
            </span>
          </div>
        )}

        {/* Status Error Message */}
        {transactionStatus === 'failed' && (
          <div className="border-grey-500 dark:border-grey-500 flex items-start gap-2 rounded-[20px] border p-4">
            <Info className="h-5 w-5 shrink-0 text-[#DE7702] dark:text-[#DE7702]" />
            <p className="text-grey-900 text-sm dark:text-red-500">
              Your card payment failed. Try another card or contact your bank.
            </p>
          </div>
        )}
      </div>

      {/* Attachments (queued batch transfer) */}
      {transactionStatus === 'queued' && transaction.attachmentFileName && (
        <div className="space-y-2">
          <span className="text-grey-600 dark:text-grey-400 text-base font-medium">
            Attachments
          </span>
          <div className="border-grey-200 dark:border-grey-700 flex items-center justify-between rounded-xl border px-4 py-3">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="text-grey-500 h-5 w-5" />
              <span className="text-grey-1000 text-sm font-medium dark:text-neutral-100">
                {transaction.attachmentFileName}
              </span>
            </div>
            <Download className="text-grey-500 h-5 w-5" />
          </div>
        </div>
      )}

      {/* Queued payment warning */}
      {transactionStatus === 'queued' && (
        <div className="border-grey-200 dark:border-grey-500 flex items-start gap-3 rounded-[20px] border p-4">
          <CircleAlert className="h-5 w-5 shrink-0 text-red-500" />
          <p className="text-grey-700 dark:text-grey-300 text-sm">
            The payment is queued due to insufficient funds, and will be processed once your
            available wallet balance is sufficient.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-4">
        {/* Receipt and Invoice buttons - responsive layout */}
        {(canShowDownloadReceiptButton || canShowInvoiceButton) && (
          <div className="flex flex-col gap-4">
            {canShowDownloadReceiptButton && (
              <Button
                variant="primary"
                className="w-full"
                onClick={onDownloadReceipt}
                isLoading={downloadReceiptMutation.isPending}
                loadingText={'Downloading receipt'}
              >
                Download receipt
              </Button>
            )}

            {canShowInvoiceButton && (
              <Button
                variant={canShowDownloadReceiptButton ? 'secondary' : 'primary'}
                className="w-full"
                onClick={onDownloadInvoice}
                isLoading={downloadInvoiceMutation.isPending}
                loadingText={'Downloading invoice'}
              >
                Download invoice
              </Button>
            )}
          </div>
        )}

        {showCancelButton && (
          <Button variant="destructive" className="w-full" onClick={onCancelClick}>
            Cancel
          </Button>
        )}

        {transactionStatus === 'queued' && !fromEntity && (
          <Link
            href={`/wallet/add?currency=${transaction.currency}`}
            className="block w-full"
            onClick={guardNavigation}
          >
            <Button
              variant="primary"
              className="flex w-full items-center gap-2 text-base font-medium sm:text-sm"
            >
              <PlusIcon className="h-5! w-5! stroke-2! sm:h-4! sm:w-4!" />
              Add funds
            </Button>
          </Link>
        )}
      </div>
      <RestrictionDialog {...restrictionDialogProps} />
    </div>
  );
}

// ============================================
// MAIN DIALOG COMPONENT
// ============================================
export function TransactionDetailsDialog({
  transaction,
  open,
  onOpenChange,
  expenseCategories,
  fromEntity,
}: TransactionDetailsDialogProps) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [transactionToCancel, setTransactionToCancel] = useState<Transaction | null>(null);
  const downloadInvoiceMutation = useDownloadTransactionInvoice();
  const downloadReceiptMutation = useDownloadTransactionReceipt();

  // Check if this is an expense transaction
  const isExpenseTransaction =
    ['wallet-to-wallet', 'internal'].includes(transaction?.type ?? '') &&
    transaction?.transfer_type?.toLowerCase() === 'expense';

  const transactionTypeDescription = transaction
    ? getTransactionTypeDescription(
        transaction.type,
        transaction.provider,
        transaction.bank_holder_name,
        transaction.transfer_type,
        transaction.is_incoming,
        transaction.description,
        transaction.user_name,
        transaction.reason
      )
    : '';

  const isNegative = transaction
    ? isNegativeTransactionType(
        transaction.type,
        transaction.currency_id,
        transaction.to_currency_id,
        transaction.transfer_type,
        transaction.is_incoming
      )
    : false;

  const transactionStatus = transaction ? getTransactionStatus(transaction.status) : 'completed';
  const shouldStrikeThrough = transactionStatus === 'failed' || transactionStatus === 'cancelled';

  const canDownloadTransactionDocuments =
    transactionStatus === 'completed' ||
    transactionStatus === 'pending' ||
    transactionStatus === 'reserved';

  const canShowInvoiceButton = Boolean(transaction && canDownloadTransactionDocuments);

  const canShowDownloadReceiptButton = Boolean(transaction && canDownloadTransactionDocuments);

  const showCancelButton = !!(
    transaction &&
    transactionStatus === 'pending' &&
    ['top-up', 'inbound'].includes(transaction.type.toLowerCase()) &&
    transaction.provider !== 'card'
  );

  const handleCancelClick = () => {
    setTransactionToCancel(transaction);
    onOpenChange(false);
    setCancelDialogOpen(true);
  };

  const handleCancelSuccess = () => {
    setCancelDialogOpen(false);
    setSuccessDialogOpen(true);
  };

  const handleDownloadInvoice = async () => {
    if (!transaction?.numericId) return;
    downloadInvoiceMutation.mutate(transaction.numericId);
  };

  const handleDownloadReceipt = () => {
    if (!transaction?.numericId) return;
    downloadReceiptMutation.mutate(transaction.numericId);
  };

  if (!transaction) {
    return (
      <>
        <CancelTransactionDialog
          open={cancelDialogOpen}
          onOpenChange={setCancelDialogOpen}
          onSuccess={handleCancelSuccess}
          transactionId={transactionToCancel?.numericId}
        />
        <CancelTransactionSuccessDialog
          open={successDialogOpen}
          onOpenChange={setSuccessDialogOpen}
        />
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-full! max-w-sm sm:max-w-md md:max-w-[600px]!">
          <DialogHeader>
            <DialogTitle className="text-grey-1000 text-[26px] font-bold dark:text-neutral-100">
              {isExpenseTransaction ? 'Expense details' : 'Transaction details'}
            </DialogTitle>
          </DialogHeader>

          {isExpenseTransaction ? (
            <ExpenseTransactionContent
              transaction={transaction}
              transactionStatus={transactionStatus}
              isNegative={isNegative}
              expenseCategories={expenseCategories}
              canShowInvoiceButton={canShowInvoiceButton}
              downloadInvoiceMutation={downloadInvoiceMutation}
              onDownloadInvoice={handleDownloadInvoice}
            />
          ) : (
            <RegularTransactionContent
              transaction={transaction}
              transactionStatus={transactionStatus}
              transactionTypeDescription={transactionTypeDescription}
              isNegative={isNegative}
              shouldStrikeThrough={shouldStrikeThrough}
              canShowInvoiceButton={canShowInvoiceButton}
              canShowDownloadReceiptButton={canShowDownloadReceiptButton}
              showCancelButton={showCancelButton}
              downloadInvoiceMutation={downloadInvoiceMutation}
              downloadReceiptMutation={downloadReceiptMutation}
              onDownloadInvoice={handleDownloadInvoice}
              onDownloadReceipt={handleDownloadReceipt}
              onCancelClick={handleCancelClick}
              fromEntity={fromEntity}
            />
          )}
        </DialogContent>
      </Dialog>

      <CancelTransactionDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onSuccess={handleCancelSuccess}
        transactionId={transactionToCancel?.numericId}
      />

      <CancelTransactionSuccessDialog
        open={successDialogOpen}
        onOpenChange={setSuccessDialogOpen}
      />
    </>
  );
}
